import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/dashboard/Header";
import ListExam from "../../components/exams/ListExam";
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useExam } from "../../hooks/useExam";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { TUser } from "../../shared/types/user.type";
import { capitalize, get, isEmpty, toNumber } from "lodash";
import { TExam } from "../../shared/types/exam.type";
import { Link, useNavigate } from "react-router-dom";
import { routes } from "../routes";
import { Ttraining } from "../../shared/types/training.type";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { useLocales } from "../../hooks/useLocales";
import { t } from "i18next";
import dayjs from "dayjs";
import {
  CalendarApi,

  EventContentArg,
  EventSourceInput,
} from "@fullcalendar/core";
import YearView from "../../components/trainings/YearView";
import RenderDateRange from "../../components/trainings/RenderDateRange";
import Duration from "dayjs/plugin/duration";
import weekOfYear from "dayjs/plugin/weekOfYear";
import palette from "../../theme/palette";
import AutocompleteHorse from "../../components/horses/AutocompleteHorse";
import { TTrainerHorse } from "../../shared/types/trainerHorse.type";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";
import allLocales from "@fullcalendar/core/locales-all";
export type Periods = "MONTH" | "WEEK" | "YEAR";
// Extend dayjs with the weekOfYear plugin
dayjs.extend(weekOfYear);
dayjs.extend(Duration);

function Trainings() {
  //State
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [selectedHorse, setSelectedHorse] = useState("");

  const calendarRef = useRef<FullCalendar | null>(null); // Reference for FullCalendar
  const calendarWeekRef = useRef<FullCalendar | null>(null); // Reference for FullCalendar

  const [date, setDate] = useState(dayjs());
  const [period, setPeriod] = useState<Periods>("MONTH");
  const { currentLang } = useLocales();
  const isUserLoading = useSelector<RootState>(
    (state) => state.user.isUserLoading,
  ) as boolean;
  const auth = useSelector<RootState>(
    (state) => state.user.user,
  ) as TUser | null;
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventSourceInput | undefined>(undefined);
  const { renderTrainingTypeAuto, renderTrainingTrackAuto } = useExam(false);
  const { track } = useUserActivityLog();

  const { exams, isExamsLoading, examsQuery } = useExam(
    false,
    get(auth, "@id"),
    null,
    undefined,
    [
      `creation_date[before]=${endDate}`,
      `creation_date[after]=${startDate}`,
      selectedHorse ? `horseTrainer.horse=${selectedHorse}` : "",
      "itemsPerPage=1000",
      `exists[horseTrainer.horse]=1`,
    ],
  );

  useEffect(() => {
    track("TRAININGS_PAGE_DISPLAYED");
  }, []);

  useEffect(() => {
    updateEvents(exams);
  }, [exams]);

  const updateEvents = (exams: Ttraining[]) => {
    setEvents(
      exams.map((exam) => ({
        id: exam.id.toString(),
        title: exam.horse_trainer.horse.name ?? "",
        start: dayjs(exam.creation_date).toISOString(),
      })),
    );
  };

  useEffect(() => {
    handleSetDate(date.format("YYYY-MM-DD"));
    handleSetWeekDate(date.format("YYYY-MM-DD"));
    updateStartEndDate(date, period);
  }, [date, period]);

  const updateStartEndDate = (date: dayjs.Dayjs, period: Periods) => {
    let start: string | undefined = undefined;
    let end: string | undefined = undefined;
    switch (period) {
      case "MONTH":
        start = date.startOf("month").toISOString();
        end = date.endOf("month").toISOString();
        break;
      case "WEEK":
        start = date.startOf("week").toISOString();
        end = date.endOf("week").toISOString();
        break;
      case "YEAR":
        start = date.startOf("year").toISOString();
        end = date.endOf("year").toISOString();
        break;
      default:
        start = undefined;
        end = undefined;
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    if (!startDate && !endDate) return;
    examsQuery.refetch();
  }, [startDate, endDate, selectedHorse]);

  const onExamClicked = (id: string) => {
    let route = routes.DASHBOARD_ROUTES.EXAM_VIEW.replace(":id", id);
    window.open(route, "_blank", "noopener,noreferrer");
  };

  const handleSetDate = (date: string) => {
    if (calendarRef.current) {
      const calendarApi: CalendarApi = calendarRef.current.getApi(); // Get FullCalendar API instance
      calendarApi.gotoDate(date); // Change the calendar's view to the new date
      // calendarApi.setOption('contentHeight',110)
    }
  };
  const handleSetWeekDate = (date: string) => {
    if (calendarWeekRef.current) {
      const calendarApi: CalendarApi = calendarWeekRef.current.getApi(); // Get FullCalendar API instance
      calendarApi.gotoDate(date); // Change the calendar's view to the new date
    }
  };

  const handleMonthClick = (month: number, exams: Ttraining[]) => {
    const updatedDate = date.month(month);
    setPeriod("MONTH");
    setDate(updatedDate);
  };

  const updateDateSelector = (date: dayjs.Dayjs) => {
    setDate(date);
  };

  const renderTypes = (args: EventContentArg) => {
    let exam = exams.find((exam) => exam.id === toNumber(args.event.id));

    if (!exam) return "";

    let track = renderTrainingTrackAuto(exam);
    let type = renderTrainingTypeAuto(exam);
    return (
      <Box display={"flex"} flexDirection={"column"}>
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {t("eap:table:headers:trainingType")} :{" "}
          <span dangerouslySetInnerHTML={{ __html: type }} />{" "}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {t("eap:table:headers:trackName")} :{" "}
          <span dangerouslySetInnerHTML={{ __html: track }} />{" "}
        </Typography>
      </Box>
    );
  };

  const handleChangedValue = (value: TTrainerHorse | null) => {
    setSelectedHorse(value ? value.horse["@id"] : "");
  };

  const renderRow = (args: EventContentArg) => {
    return (
      <Tooltip arrow placement="top" title={renderTypes(args)}>
        <Box
          onClick={() => onExamClicked(args.event.id)}
          sx={{
            display: "flex",
            gap: "9px",
            backgroundColor: "rgba(0, 0, 0,.1)",
            alignItems: "center",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          <Box
            width={5}
            minWidth={5}
            height={"100%"}
            bgcolor={palette.light.primary.main}
          />
          <Typography
            variant="subtitle2"
            fontSize={"12px"}
            sx={{
              paddingBottom: "3px",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              wordBreak: "break-all",
            }}
          >
            {args.event.title}
          </Typography>
        </Box>
      </Tooltip>
    );
  };
  return (
    <>
      <Header text="navbar:myTrainings"></Header>
      <Divider sx={{ my: 2 }}></Divider>
      <Box
        mb={2}
        display={"flex"}
        flexDirection={{ md: "row", xs: "column-reverse" }}
        justifyContent={"space-between"}
        alignItems={{ md: "center", xs: "flex-start" }}
        gap={2}
        flexWrap={"wrap"}
      >
        <RenderDateRange
          date={date}
          handleDateChange={updateDateSelector}
          period={period}
        />
        <Box sx={{ width: { md: "300px", xs: "100%" } }}>
          <AutocompleteHorse handleChangedValue={handleChangedValue} />
        </Box>

        <ButtonGroup>
          <Button
            onClick={() => {
              setPeriod("WEEK");
            }}
            variant={period === "WEEK" ? "contained" : "outlined"}
          >
            {capitalize(t("thirdSection:weekShort"))}
          </Button>
          <Button
            onClick={() => {
              setPeriod("MONTH");
            }}
            variant={period === "MONTH" ? "contained" : "outlined"}
          >
            {capitalize(t("thirdSection:monthShort"))}
          </Button>
          <Button
            onClick={() => {
              setPeriod("YEAR");
            }}
            variant={period === "YEAR" ? "contained" : "outlined"}
          >
            {capitalize(t("thirdSection:yearShort"))}
          </Button>
        </ButtonGroup>
      </Box>
      {period === "YEAR" && (
        <YearView exams={exams} onMonthClicked={handleMonthClick} />
      )}
      <Box display={period === "MONTH" ? "initial" : "none"}>
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          height={"550px"}
          contentHeight={"80px"}
          headerToolbar={false}
          ref={calendarRef}
          dayMaxEvents={1}
          weekends
          events={events}
          locales={allLocales}
          locale={currentLang}
          eventContent={renderRow}
          eventClick={(info) => {
            info.jsEvent.preventDefault();
            onExamClicked(info.event.id);
          }}
        />
      </Box>

      <Box display={period === "WEEK" ? "initial" : "none"}>
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="customWeek"
          views={{
            customWeek: {
              type: "dayGrid",
              duration: { weeks: 1 },
            },
          }}
          contentHeight={"160px"}
          // showNonCurrentDates
          firstDay={1}
          headerToolbar={false}
          ref={calendarWeekRef}
          locales={allLocales}
          dayMaxEvents={4}
          weekends
          events={events}
          locale={currentLang}
          eventContent={renderRow}
        />
      </Box>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={isExamsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default Trainings;
